// The Daily Live Price across the week, every class — the homepage's Live
// Prices view.
//
// Owner, 2026-10-08, designing it from a mock board: one row per class,
// the last six sale days plus today across, the seven-day index on the
// right. Each cell: the price, head on the face, day over day, and the
// same-weekday change ("Tuesday vs Tuesday"). Today's column is alive — a
// slow, subtle breathe the whole time so it reads as the live one, a wash
// and rolling digits on an actual change, so a pulse never looks like a
// move. "Are we using these prices for our board but with every weight
// class?" — yes: the same finals and the same live reading as the daily
// table, through rb_daily_price_board; nothing recomputed.
//
// Plain <a> links only: this renders inside the routerless homepage.

import { useEffect, useState } from "react";

import { type DailyBoardClass, type DailyBoardDay, fetchDailyBoard } from "@/lib/rbDailyPrice";
import LiveBeat from "../cattle-ticker/LiveBeat";
import { Odometer } from "../cattle-ticker/TickerParts";
import { useFlash } from "../cattle-ticker/useFlash";
import { cnt, px, signedPx } from "./quoteFormat";
import "./indexPage.css";

const POLL_MS = 20_000;

function dayHead(d: DailyBoardDay): { top: string; sub: string } {
  const dt = new Date(`${d.asOf}T12:00:00Z`);
  const wd = dt.toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" });
  const md = dt.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
  return { top: d.live ? "Live" : wd, sub: md };
}
function clockCt(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/Chicago" });
}
function rowLabel(c: DailyBoardClass): string {
  if (!c.isHeadline && c.weightMin != null && c.weightMax != null) return `${c.weightMin}–${c.weightMax - 1} lb`;
  return c.name.replace(/^(RB|DVA) (Real Time )?/, "").replace(/ Index$/, "");
}

/** One day's cell: the price, head under it, and one line with both changes
 *  side by side (owner, 2026-10-08: "I liked your first one better, with the
 *  day over day and week over week in the same column… this is way too many
 *  columns… just make it look better"). The live one flashes on a change and
 *  rolls its digits. */
function PriceCell({ c, d }: { c: DailyBoardClass; d: DailyBoardDay }) {
  const flash = useFlash(d.live ? d.price : null);
  const href = `/index/${c.slug}/daily/${d.asOf}`;
  const thin = d.publication === "INSUFFICIENT_VOLUME";
  const head = d.head ?? 0;
  const dir = (v: number | null) => (v == null ? undefined : v > 0 ? "up" : v < 0 ? "down" : "flat");
  const title = d.live && c.lastAt && head > 0 ? `${cnt(d.lots ?? 0)} lots · last lot ${clockCt(c.lastAt)}` : head > 0 ? `${cnt(d.lots ?? 0)} lots` : undefined;
  return (
    <td className="rbdg-c" data-live={d.live ? "1" : undefined} data-flash={flash ?? undefined} data-dash={d.price === null ? "1" : undefined}>
      <a href={href} className="rbdg-cell" title={title} aria-label={`${rowLabel(c)}, ${d.asOf}`}>
        <span className="rbdg-px">
          {d.price !== null ? (d.live ? <Odometer text={px(d.price)} /> : px(d.price)) : thin ? "thin" : d.live && head > 0 ? "…" : "—"}
        </span>
        <span className="rbdg-hd">{head > 0 ? `${cnt(head)} hd` : thin ? "too few head" : d.live ? "no lots yet" : "no sales"}</span>
        <span className="rbdg-chg2">
          <span data-dir={dir(d.change)}><i>d/d</i>{d.change == null ? "—" : signedPx(d.change)}</span>
          <span data-dir={dir(d.changeWk)}><i>w/w</i>{d.changeWk == null ? "—" : signedPx(d.changeWk)}</span>
        </span>
      </a>
    </td>
  );
}

function IndexCell({ v }: { v: number | null }) {
  const flash = useFlash(v);
  return <td className="rbdg-c rbdg-ix" data-flash={flash ?? undefined}><span className="rbdg-px">{v !== null ? <Odometer text={px(v)} /> : "—"}</span></td>;
}

function Row({ c }: { c: DailyBoardClass }) {
  return (
    <tr>
      <th scope="row" className="rbdg-row">
        <a href={`/index/${c.slug}`} title={`${c.code}-D · ${c.name}`}><b>{rowLabel(c)}</b></a>
      </th>
      {c.days.filter((d) => !d.sunday).map((d) => <PriceCell key={d.asOf} c={c} d={d} />)}
      <IndexCell v={c.indexCwt} />
    </tr>
  );
}

export default function DailyPriceGrid() {
  const [rows, setRows] = useState<DailyBoardClass[] | null>(null);
  const [at, setAt] = useState<number | null>(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let on = true;
    const pull = () => {
      fetchDailyBoard(7)
        .then((r) => { if (on && r.length) { setRows(r); setAt(Date.now()); setErr(false); } })
        .catch(() => { if (on && rows === null) setErr(true); });
    };
    pull();
    const t = window.setInterval(pull, POLL_MS);
    return () => { on = false; window.clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (err && rows === null) return <p className="rbq-msg">The weekly board could not be loaded.</p>;
  if (rows === null) return null;
  // Sale days only (owner, 2026-10-08, "less busy"): a Sunday column is a
  // column of dashes every week. The reader asks for seven so six remain.
  const days = (rows[0]?.days ?? []).filter((d) => !d.sunday);
  const bySex = (sex: string) => rows.filter((c) => !c.isHeadline && c.cattleType === sex).sort((a, b) => (a.weightMin ?? 0) - (b.weightMin ?? 0));
  const groups: [string, DailyBoardClass[]][] = [
    ["Steers", bySex("Steers")],
    ["Heifers", bySex("Heifers")],
    ["Headline classes", rows.filter((c) => c.isHeadline)],
  ];
  return (
    // rbdp-home carries the table's navy-header variables on the homepage
    // (the SPA's index page sets them at the root; this document does not).
    <div className="rbdp-home">
    <section className="rbq rbdg" aria-label="Daily Live Price, every class, this week">
      <div className="rbq-bar">
        {/* "Is it really today's lots only? That's what the header says."
            (owner, 2026-10-08) — each column is that one day's lots, not
            the seven the index rolls up; the live column is today's. */}
        <h2>Daily Live Price <span>$/cwt · head-weighted · each day&rsquo;s lots on their own, not the seven-day index</span></h2>
        <LiveBeat at={at} />
      </div>
      <div className="rbq-tw rbdg-tw">
        <table className="rbq-t rbdg-t">
          <thead>
            <tr>
              <th className="rbdg-row">Class</th>
              {days.map((d) => {
                const h = dayHead(d);
                return (
                  <th key={d.asOf} className="rbdg-dh" data-live={d.live ? "1" : undefined}>
                    <b>{d.live && <i className="rbdg-dot" aria-hidden="true" />}{h.top}</b><span>{h.sub}</span>
                  </th>
                );
              })}
              <th className="rbdg-dh rbdg-ixh"><b>7-day</b><span>index</span></th>
            </tr>
          </thead>
          {groups.map(([label, list]) => list.length === 0 ? null : (
            <tbody key={label}>
              <tr className="rbdg-grp"><th scope="rowgroup" colSpan={days.length + 2}>{label}</th></tr>
              {list.map((c) => <Row key={c.slug} c={c} />)}
            </tbody>
          ))}
        </table>
      </div>
      {/* No note under the grid (owner, 2026-10-14: "remove this paragraph as well"). */}
    </section>
    </div>
  );
}
